// ============================================================
// Login Page
// ============================================================

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormField } from '@/components/forms/FormField';
import { useAuth } from '@/hooks/useAuth';
import { tokens } from '@/theme/tokens';
import { ROLES, ROLE_LABELS } from '@/constants/roles';
import { mockUsers } from '@/mock/users';

import { PATHS } from '@/router/paths';
import { buildDynamicNavigation } from '@/utils/dynamicNavigation';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(4, 'Password must be at least 4 characters')
    .required('Password is required'),
});

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const targetPath = location.state?.from?.pathname && location.state.from.pathname !== '/'
    ? location.state.from.pathname
    : PATHS.DASHBOARD;

  const handleSubmit = async (values, { setSubmitting }) => {
    setError('');
    try {
      const res = await login(values.email, values.password);
      toast.success('Welcome back!');
      
      const userRole = String(res.data?.user?.role || '').toUpperCase();
      if (userRole === 'SUPER_ADMIN' || userRole === ROLES.SUPER_ADMIN) {
        navigate(PATHS.SUPER_ADMIN_DASHBOARD, { replace: true });
      } else if (userRole === 'ORGANIZATION_ADMIN' || userRole === 'ORG_ADMIN') {
        const dest = (targetPath && targetPath !== '/' && targetPath !== PATHS.DASHBOARD) ? targetPath : PATHS.DASHBOARD;
        navigate(dest, { replace: true });
      } else {
        // Non-OrgAdmin roles: determine first available permitted path from backend modules
        const activeMods = res.data?.modules || res.modules || [];
        const navItems = buildDynamicNavigation(activeMods, userRole);
        const firstNavPath =
          navItems[0]?.children?.[0]?.path ||
          (navItems[0]?.path && navItems[0].path !== '#' ? navItems[0].path : '/dashboard');

        const dest = (targetPath && targetPath !== '/' && targetPath !== PATHS.DASHBOARD)
          ? targetPath
          : firstNavPath;
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const quickLogin = (email) => {
    const form = document.querySelector('form');
    if (form) {
      // Programmatically submit with the selected email
      handleSubmit(
        { email, password: 'demo1234' },
        { setSubmitting: () => {} }
      );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" fontWeight={800} sx={{ mb: 1, color: tokens.colors.text.primary, letterSpacing: '-0.01em' }}>
        Welcome Back 👋
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Sign in to your account to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={loginSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Box sx={{ mb: 2 }}>
              <FormField
                name="email"
                label="Email Address"
                type="email"
                placeholder="Enter your email"
              />
            </Box>

            <Box sx={{ position: 'relative', mb: 1 }}>
              <FormField
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
              <Typography
                component={Link}
                to="/forgot-password"
                variant="body2"
                fontWeight={600}
                sx={{
                  color: tokens.colors.primary.main,
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  '&:hover': { color: tokens.colors.primary.dark },
                }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isSubmitting}
              startIcon={<LogIn size={20} />}
              sx={{ 
                py: 1, 
                fontSize: '0.95rem', 
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 118, 255, 0.23)',
                }
              }}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
